import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Card, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AgeGateContext } from '@subflow-frontend/sections/listing/AgeGateContext';
import { AgeGateInput } from '@subflow-frontend/sections/listing/AgeGateModal';
import { Product, SubscriberProductResponseDto } from '@subflow/api-client';
import {
  isApiError,
  useCreateSubscriptionToProduct,
  useSubscriberProfile,
} from '@subflow/data-access';
import dayjs, { isDayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import 'yup-phone';
import OptIn from '../../../../components/OptIn';
import {
  FormProvider,
  RHFPhoneNumberField,
  RHFTextField,
} from '../../../../components/hook-form';
import { PhoneSchema } from '../../../../utils/phone';
import { useSession } from 'next-auth/react';
import { isSubscriberUser } from '@subflow-frontend/guards/SubscriberGuard';
import { formatSubscriberName } from '@subflow-frontend/utils/subscriber';

const RootStyle = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3, 0),
  },
  [theme.breakpoints.up(1368)]: {
    padding: theme.spacing(5, 8),
  },
}));

interface FormValuesProps {
  name: string;
  email: string;
  phone: string;
  agreeTerms: boolean;
  birthDate: dayjs.Dayjs | null;
}

// ----------------------------------------------------------------------

type Props = {
  product: Product | SubscriberProductResponseDto;
  successCallback?: () => void;
};

export default function SubscriptionSimplePrice({
  product,
  successCallback = () => {
    return;
  },
}: Props) {
  const { ageRestrictionActive, ageRestrictionValue, birthDate } =
    useContext(AgeGateContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [phoneCountryZone, setPhoneCountryZone] = useState<string>('US');
  const { status, data } = useSession();
  const { data: subscriber } = useSubscriberProfile(
    status != 'authenticated' || !isSubscriberUser(data)
  );
  const { mutateAsync } = useCreateSubscriptionToProduct();

  const NewCustomerSchema = Yup.object().shape({
    ...(!subscriber && {
      name: Yup.string().required('Name is required'),
      email: Yup.string()
        .email('Invalid email')
        .required('Required')
        .lowercase(),
      phone: PhoneSchema.phone(
        phoneCountryZone,
        false,
        'Must be a valid mobile number'
      ),
    }),
    agreeTerms: Yup.bool().oneOf([true]),
    ...(ageRestrictionActive
      ? {
          birthDate: Yup.object()
            .nullable()
            .test('date-required', 'Date is required!', (value) => {
              if (isDayjs(value)) {
                return value.isValid();
              } else {
                return false;
              }
            })
            .test(
              'is-old-enough',
              `You must be at least ${ageRestrictionValue} years old to access this content.`,
              function (value) {
                if (isDayjs(value)) {
                  const now = dayjs();
                  const ageDiff = now.diff(value, 'year');
                  return ageDiff >= ageRestrictionValue;
                } else {
                  return false;
                }
              }
            ),
        }
      : {}),
  });

  const defaultValues = useMemo(() => {
    return subscriber
      ? {
          name: formatSubscriberName(subscriber.firstName, subscriber.lastName),
          email: subscriber.email,
          phone: subscriber.phone,
          agreeTerms: false,
          ...(ageRestrictionActive ? { birthDate: birthDate } : {}),
        }
      : {
          name: '',
          email: '',
          phone: '',
          agreeTerms: false,
          ...(ageRestrictionActive ? { birthDate: birthDate } : {}),
        };
  }, [ageRestrictionActive, birthDate, subscriber]);

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(NewCustomerSchema),
    defaultValues,
  });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = async ({ name, email, phone, ...values }: any) => {
    const phoneWithCountryCode = `+${phone}`;
    setIsLoading(true);

    try {
      const response = await mutateAsync({
        productId: product.id,
        body: {
          name,
          email,
          phone: phoneWithCountryCode,
        },
      });
      setIsSuccessful(response.status === 'active');
    } catch (error) {
      let errorMessage;
      if (error.statusCode === 409) {
        errorMessage = error.message;
      } else if (error.message === 'upgrade required') {
        errorMessage = 'Upgrade your plan to unlock the full experience';
      } else {
        errorMessage = 'There was an error subscribing to this product.';
      }

      if (isApiError(error) && error.errorMessage) {
        errorMessage = error.errorMessage;
      }

      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccessful) {
    successCallback();
  }

  const onCountryChange = (country) => setPhoneCountryZone(country);

  useEffect(() => {
    if (birthDate) {
      methods.setValue('birthDate', birthDate);
    }
  }, [birthDate]);
  return (
    <RootStyle>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <Stack spacing={3} mx={3} pb={3}>
            <div></div>
            <RHFTextField
              disabled={!!subscriber}
              name="name"
              label="Name"
              inputProps={{
                'data-testid': 'subscription-simple-price-name',
              }}
            />
            <RHFTextField
              disabled={!!subscriber}
              name="email"
              label="Email"
              type="email"
              autoComplete="on"
              inputProps={{
                'data-testid': 'subscription-simple-price-email',
              }}
            />

            <RHFPhoneNumberField
              disabled={!!subscriber}
              label="Mobile Phone"
              name="phone"
              whitelistCountries={['us', 'ca']}
              onCountryChange={onCountryChange}
              inputProps={{
                'data-testid': 'subscription-simple-price-phone',
              }}
            />
            {ageRestrictionActive && (
              <>
                <Typography variant="caption">
                  You must be at least {ageRestrictionValue} years old to access
                  the following content.
                </Typography>

                <AgeGateInput name="birthDate" label="Birth Date" />
              </>
            )}
            <OptIn merchantName={product?.merchant?.businessName} />

            <LoadingButton
              type="submit"
              disabled={isLoading}
              variant="contained"
              sx={{ mt: 2 }}
              data-testid="subscription-simple-price-submit-button"
            >
              Subscribe
            </LoadingButton>
          </Stack>
        </Card>
      </FormProvider>
    </RootStyle>
  );
}
